import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Paper } from '@mui/material';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ContentArea from '../components/content-area/Content';
import LocalText from '../components/common/LocalText';
import GameScoreboardSection from '../components/gameScoreBoard/GameScoreboardSection';
import GameAnnouncementSection from '../components/gameAnnouncement/GameAnnouncementSection';
import { usePermissions } from '../hooks/usePermissions';
import { cardBase } from '../theme/adminTokens';

// Same pill tab-switcher shape as Items.jsx's TABS (Item Catalog / Discount Scheduler /
// Free Trial / Prizes) — reused here rather than inventing a second tab style. Each key
// gates one of the two Paper sections below; there's no tab.route since both stay on this
// same page.
const TABS = [
  { key: 'scoreboard', label: 'Game Scoreboard', icon: <LeaderboardOutlinedIcon sx={{ fontSize: 14 }} /> },
  { key: 'announcements', label: 'Game Announcements', icon: <CampaignOutlinedIcon sx={{ fontSize: 14 }} /> },
];

export default function GameScoreBoard() {
  const location = useLocation();
  const canEdit = usePermissions().canFeature('/game-score-board', 'game-score-board-edit');

  // Deep-link support (location.state?.activeTab), same convention as Items.jsx — nothing
  // currently navigates here with a preset tab, but this keeps the pattern consistent for
  // whenever something (e.g. a future "view its announcements" link) wants to.
  const [activeTab, setActiveTab] = useState(location.state?.activeTab ?? 'scoreboard');

  const tabSwitcher = (
    <Box sx={{
      display: 'flex', bgcolor: '#f1f5f9', p: '4px', borderRadius: '10px',
      border: '1px solid #e2e8f0', gap: '2px',
      width: { xs: '100%', sm: 'auto' },
    }}
    >
      {TABS.map((tab) => {
        const active = activeTab === tab.key;
        return (
          <Box
            key={tab.key}
            component="button"
            type="button"
            onClick={() => setActiveTab(tab.key)}
            sx={{
              px: 2, py: 0.875, borderRadius: '8px', cursor: 'pointer', border: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
              fontSize: 11, fontWeight: 700, fontFamily: 'inherit', transition: 'all 0.15s', userSelect: 'none',
              flex: { xs: 1, sm: 'none' },
              bgcolor: active ? 'white' : 'transparent',
              color: active ? '#2563eb' : '#64748b',
              boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              outline: active ? '1px solid #bfdbfe' : '1px solid transparent',
              '&:hover': { color: active ? '#2563eb' : '#475569', bgcolor: active ? 'white' : '#e8edf4' },
            }}
          >
            {tab.icon}
            <LocalText>{tab.label}</LocalText>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <ContentArea title="Game Score Board" titleAction={tabSwitcher}>
      {/* Both sections stay mounted (display, not conditional render) rather than tearing
          down on tab switch — GameScoreboardSection's own useEffect auto-selects its first
          game on mount; unmounting it on every switch away from the Scoreboard tab would risk
          that auto-select never having run yet if this page is ever deep-linked straight to
          activeTab: 'announcements'. Keeping both mounted also preserves each section's own
          filters/in-progress state (an open detail modal, scoreboard search text, each
          section's own selected game) across tab switches, for free. The two sections each
          own an independent Game selector now — picking a game in one no longer affects the
          other.
          flexGrow: 1 — ContentArea's own wrapper is already a flex column filling the page,
          so whichever card is visible stretches to fill the remaining height itself instead
          of sizing to its (possibly tiny, 1-row) content and leaving the page's own gray
          background exposed below it. Adapts to any screen size automatically since it's
          real flex distribution, not a hardcoded min-height guess. */}
      <Paper elevation={0} sx={{ ...cardBase, '&:hover': {}, p: { xs: 2, sm: 3 }, mb: 3, flexGrow: 1, display: activeTab === 'scoreboard' ? 'block' : 'none' }}>
        <GameScoreboardSection />
      </Paper>

      <Paper elevation={0} sx={{ ...cardBase, '&:hover': {}, p: { xs: 2, sm: 3 }, flexGrow: 1, display: activeTab === 'announcements' ? 'block' : 'none' }}>
        {/* Same SpaceAdmin/Marketing gate as the feature-key check above — matches
            GameAnnouncementController's CallerCanManage role check on the backend. */}
        <GameAnnouncementSection canManage={canEdit} />
      </Paper>
    </ContentArea>
  );
}
